/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import axios from 'axios';
import L, { FeatureGroup } from 'leaflet';
import ReactDOM from 'react-dom';
import { useLocation, useParams } from 'react-router-dom';
import { parse } from 'querystring';
import randomCode from 'random-words';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { Switch } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { centroid, circle, booleanIntersects } from '@turf/turf';
import { CardHeader } from '../../../components/CardHeader';
import { Drawer } from '../../../components/Drawer';
import { Zone } from '../markerTemplate';
import { ZoneCard } from './ZoneCard';
import { SearchInput } from '../../../components/SearchInput';
import compareStrings from '../../../utils/compareStrings';
import { AddButton } from '../../../components/AddButton';

const initialValues = {
  name: '',
  description: '',
};

function EditZoneSideMenu({
  intl,
  open,
  mapRef,
  onClose,
  hasPermission,
  storeZones,
  getDistFromOrigin,
  isDragging,
}) {
  const pathParameters = useParams();
  const { search } = useLocation();
  const queryString = parse(search.replace('?', ''));
  const { areaId, facilityId, floorPlanId } = pathParameters;
  const [zones, setZones] = useState([]);
  const [filteredZones, setFilteredZones] = useState([]);
  const [displayTooltips, setDisplayTooltips] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const drawnItems = useRef(new FeatureGroup());
  const drawControl = useRef(
    new L.Control.Draw({
      edit: {
        featureGroup: drawnItems.current,
        remove: false,
      },
      draw: {
        polygon: false,
        polyline: false,
        marker: false,
        circlemarker: false,
        circle: false,
        rectangle: false,
      },
    })
  );

  useEffect(() => {
    const displayTooltips = localStorage.getItem('displayZoneLabel');
    setDisplayTooltips(displayTooltips === 'false' ? false : true);
    getZones();
  }, []);

  useEffect(() => {
    if (mapRef) {
      mapRef.addControl(drawControl.current);
      mapRef.on(L.Draw.Event.CREATED, e => {
        const layer = e.layer;
        let hasSomeOverlapping = false;
        drawnItems.current.eachLayer(lay => {
          let geoJSON = layer.toGeoJSON();
          if (layer instanceof L.Circle) {
            const circleGeoJSON = layer.toGeoJSON();
            geoJSON = circle(
              circleGeoJSON.geometry.coordinates,
              layer.getRadius()
            );
          }
          if (!hasSomeOverlapping) {
            hasSomeOverlapping = booleanIntersects(geoJSON, lay.toGeoJSON());
          }
        });
        if (hasSomeOverlapping) {
          enqueueSnackbar(intl.formatMessage({ id: 'CANNOT_OVERLAP_ZONES' }), {
            variant: 'warning',
          });
          return;
        }
        if (
          layer instanceof L.Polyline &&
          layer.getLatLngs &&
          !Array.isArray(layer.getLatLngs()[0])
        )
          return;
        drawnItems.current.eachLayer(lay => {
          if (!lay.databaseId) lay.remove();
        });
        mapRef.addLayer(layer);
        drawnItems.current.addLayer(layer);
        const el = document.createElement('div');
        ReactDOM.render(
          <Zone
            intl={intl}
            zone={{
              ...initialValues,
              externalId: randomCode({ exactly: 2, join: '-' }),
            }}
            hasPermission={hasPermission}
            params={pathParameters}
            afterSubmit={afterSubmit}
            onCancel={onCancel}
            onRemove={onRemove}
            geoJSON={getGeoJSON}
            getDistFromOrigin={getDistFromOrigin}
          />,
          el
        );
        layer.bindPopup(el).openPopup();
        drawControl.current.setDrawingOptions({
          polygon: false,
          circle: false,
        });
      });
      mapRef.on('popupopen', e => {
        const marker = e.popup._source;
        drawnItems.current.eachLayer(x => {
          if (x.databaseId === marker.databaseId) {
            x.setStyle({
              color: '#FF591C',
              fillOpacity: 0.4,
            });
          }
        });
      });
      mapRef.on('popupclose', e => {
        const marker = e.popup._source;
        drawnItems.current.eachLayer(x => {
          if (x.databaseId === marker.databaseId) {
            x.setStyle({
              color: '#FF9600',
              fillOpacity: 0.2,
            });
          }
        });
      });
    }
  }, [mapRef]);

  useEffect(() => {
    if (mapRef) {
      setFilteredZones(zones);
      zones.forEach(addMarker);
      mapRef.on(L.Draw.Event.EDITED, ({ layers }) => {
        const newValues = [];
        Object.values(layers._layers).forEach(lay => {
          const layer = zones.find(x => x.id === lay.databaseId);
          if (layer) {
            const geoJson = lay.toGeoJSON();
            if (lay instanceof L.Circle)
              geoJson.properties.radius = lay.getRadius();
            const zone = {
              ...layer,
              geoJson,
            };
            zone.geoJsonFromOrigin = getDistFromOrigin(zone);
            newValues.push(zone);
            axios.put(
              `/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}/zones/${layer.id}`,
              zone
            );
          }
        });
        drawnItems.current.eachLayer(lay => {
          lay.remove();
        });
        const oldValues = zones.filter(
          x => !newValues.find(y => x.id === y.id)
        );
        setZones([...oldValues, ...newValues]);
      });
      if (!displayTooltips) {
        drawnItems.current.eachLayer(layer => layer.closeTooltip());
      }
    }
  }, [zones, mapRef]);

  useEffect(() => {
    if (isDragging) {
      drawnItems.current.eachLayer(lay => {
        L.DomUtil.addClass(lay._path, 'disable-events');
      });
    } else {
      drawnItems.current.eachLayer(lay => {
        L.DomUtil.removeClass(lay._path, 'disable-events');
      });
    }
  }, [isDragging]);

  async function getZones() {
    const { data } = await axios.get(
      `/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}/zones`
    );
    if (data) {
      setZones(data);
      setFilteredZones(data);
      storeZones(data);
      flickLayer(+queryString.zoneId);
    }
  }

  function addNonGroupLayers(sourceLayer, id) {
    if (sourceLayer instanceof L.LayerGroup) {
      sourceLayer.eachLayer(layer => {
        addNonGroupLayers(layer, id);
      });
    } else {
      sourceLayer.databaseId = id;
      drawnItems.current.addLayer(sourceLayer);
    }
  }

  function addMarker(item) {
    const layer = L.geoJSON(item.geoJson, {
      style: {
        color: '#FF9600',
      },
      pointToLayer: (feature, latlng) => {
        if (feature.properties.radius) {
          return new L.Circle(latlng, feature.properties.radius);
        }
      },
      onEachFeature: (_, layer) => {
        layer.databaseId = item.id;
        drawnItems.current.addLayer(layer);
        layer.bindTooltip(item.name, { permanent: true });
        layer.on('preclick', e => {
          const el = document.createElement('div');
          ReactDOM.render(
            <Zone
              intl={intl}
              zone={item}
              hasPermission={hasPermission}
              params={pathParameters}
              afterSubmit={afterSubmit}
              onCancel={onCancel}
              onRemove={onRemove}
              geoJSON={getGeoJSON}
              getDistFromOrigin={getDistFromOrigin}
            />,
            el
          );
          layer.bindPopup(el);
          mapRef.closePopup();
        });
        const leafletTopElements = document.querySelectorAll('div.leaflet-top');
        const leafletBottomElements = document.querySelectorAll(
          'div.leaflet-bottom'
        );
        layer.on('popupopen', () => {
          leafletTopElements.forEach(element => {
            element.style.opacity = 0;
          });
          leafletBottomElements.forEach(function(element) {
            element.style.opacity = 0;
          });
        });
        layer.on('popupclose', () => {
          leafletTopElements.forEach(element => {
            element.style.opacity = 1;
          });
          leafletBottomElements.forEach(element => {
            element.style.opacity = 1;
          });
        });
      },
    });
    layer.addTo(mapRef);
    addNonGroupLayers(layer, item.id);
  }

  function addZone() {
    drawControl.current.setDrawingOptions({
      polygon: {
        shapeOptions: {
          color: '#C65D30',
          fillOpacity: 0.4,
        },
      },
      circle: {
        shapeOptions: {
          color: '#C65D30',
          fillOpacity: 0.4,
        },
      },
    });
    mapRef.removeControl(drawControl.current);
    mapRef.addControl(drawControl.current);
    drawnItems.current.eachLayer(lay => {
      lay.editing.disable();
    });
    onClose();
  }

  function onRemove(zoneId) {
    if (!zoneId) {
      drawnItems.current.eachLayer(lay => {
        if (!lay.databaseId) lay.remove();
      });
    }
    drawControl.current.setDrawingOptions({
      polygon: false,
      circle: false,
    });
    if (zoneId) {
      drawnItems.current.eachLayer(lay => {
        lay.remove();
      });
      const newValues = zones.filter(x => zoneId !== x.id);
      setZones(newValues);
      storeZones(newValues);
    }
    mapRef.closePopup();
    // onCancel()
    mapRef.removeControl(drawControl.current);
    mapRef.addControl(drawControl.current);
  }

  function afterSubmit(val, isUpdate) {
    if (!isUpdate) {
      drawnItems.current.eachLayer(lay => {
        lay.remove();
        // lay.editing.disable()
      });
      onRemove();
      setZones(values => {
        storeZones(values.concat(val));
        return values.concat(val);
      });
    } else {
      drawControl.current.setDrawingOptions({
        polygon: false,
        circle: false,
      });
      mapRef.removeControl(drawControl.current);
      mapRef.addControl(drawControl.current);

      const newValues = zones.map(zone => {
        if (zone.id === val.id) return val;
        else return zone;
      });
      drawnItems.current.eachLayer(lay => {
        lay.remove();
        // lay.editing.disable()
      });
      setZones(newValues);
      storeZones(newValues);
      mapRef.closePopup();
    }
  }

  function onCancel(id) {
    mapRef.closePopup();
    drawnItems.current.eachLayer(lay => {
      if (!id) {
        if (!lay.databaseId) {
          lay.remove();
          drawControl.current.setDrawingOptions({
            polygon: false,
            circle: false,
          });
          mapRef.removeControl(drawControl.current);
          mapRef.addControl(drawControl.current);
        }
      } else {
        lay.remove();
        const newLayers = [...zones];
        setZones(newLayers);
        storeZones(newLayers);
      }
    });
  }

  function getGeoJSON(id) {
    let geoJson;
    drawnItems.current.eachLayer(lay => {
      if (lay.databaseId === id) {
        geoJson = lay.toGeoJSON();
        if (lay instanceof L.Circle)
          geoJson.properties.radius = lay.getRadius();
      }
    });

    return geoJson;
  }

  function flickLayer(id) {
    drawnItems.current.eachLayer(async lay => {
      if (lay.databaseId === id) {
        const [long, lat] = centroid(
          lay.toGeoJSON().geometry
        ).geometry.coordinates;
        mapRef.flyTo([lat, long], 3);
        await new Promise(resolve => setTimeout(resolve, 850));
        lay.setStyle({
          color: '#C65D30',
          fillOpacity: 0.4,
        });
        await new Promise(resolve => setTimeout(resolve, 100));
        lay.setStyle({
          color: '#FF9600',
        });
        await new Promise(resolve => setTimeout(resolve, 100));
        lay.setStyle({
          color: '#C65D30',
        });
        await new Promise(resolve => setTimeout(resolve, 100));
        lay.setStyle({
          color: '#FF9600',
        });
        await new Promise(resolve => setTimeout(resolve, 100));
        lay.setStyle({
          color: '#C65D30',
        });
        await new Promise(resolve => setTimeout(resolve, 100));
        lay.setStyle({
          color: '#FF9600',
          fillOpacity: 0.2,
        });
      }
    });
  }

  function changeText(val) {
    setFilteredZones(zones.filter(x => compareStrings(JSON.stringify(x), val)));
  }

  function displayAndHideTooltips() {
    const shouldDisplay = !displayTooltips;
    drawnItems.current.eachLayer(layer => {
      if (shouldDisplay) layer.openTooltip();
      else layer.closeTooltip();
    });
    localStorage.setItem('displayZoneLabel', shouldDisplay);
    setDisplayTooltips(shouldDisplay);
  }

  return (
    <Drawer
      onClose={() => {
        setFilteredZones(zones);
        onClose();
      }}
      containerId="floor-plan-container"
      open={open}
    >
      <div className="h-100" style={{ width: 400 }}>
        <div className="h-100 px-0 card">
          <CardHeader
            title={intl.formatMessage({ id: 'ZONES' })}
            subtitle={intl.formatMessage({ id: 'ZONES.SUBTITLE' })}
            btn={
              hasPermission('zones*', 'C') && <AddButton onClick={addZone} />
            }
            iconPath={'/media/svg/senseworks/zone.svg'}
          />
          <div className="d-flex flex-column h-100">
            <div className="px-4 pt-2">
              <SearchInput onChange={changeText} />
            </div>
            <div
              className="px-4 py-2"
              style={{ borderBottom: 'solid 1px #AAAAAA' }}
            >
              <label htmlFor="showTooltip">
                {' '}
                {intl.formatMessage({ id: 'DISPLAY_HIDE_ZONE_TOOLTIP' })}{' '}
              </label>
              <Switch
                id="showTooltip"
                onChange={displayAndHideTooltips}
                checked={displayTooltips}
              />
            </div>
            <div className="d-flex flex-column justify-content-between h-100">
              <div>
                {filteredZones.map(op => (
                  <ZoneCard
                    key={op.id}
                    body={op.description}
                    title={op.name}
                    onClick={() => flickLayer(op.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
}

export default injectIntl(EditZoneSideMenu);
